// src/pages/MobileTabSelect.js
//
// Mobile tab dropdown (2026-10-28). Below 768px, horizontally-scrolling tab
// rows hide most of their tabs off-screen, so each tab row on a detail page
// is replaced with a single native <select> (same approach as App.js's
// object-type picker in the nav search). A native select gives iOS/Android
// their own full-screen picker, which is far easier to tap than a scrolling
// row of small buttons.
//
// Callers keep their existing index-based tab state unchanged; this
// component only swaps the control used to change it:
//
//   isMobile
//       ? <MobileTabSelect tabs={labels} active={i} onChange={setI} />
//       : <div style={styles.tabBar}>…buttons…</div>
//
// Variants:
//   'tab' (default) — a top-level tab row (info tabs, parent tabs). Rounded
//                     top corners and a purple bottom edge (echoing the
//                     desktop active-tab underline) so it sits flush on top
//                     of the content panel below it, whose borderTop is none.
//   'sub'           — a sub-tab row inside a tab's content (Relationships,
//                     Locations, Flyers, Photos…). A standalone, fully
//                     rounded control, visually one level down.
//
// Font size is 16px so focusing it never triggers iOS Safari's auto-zoom
// (also enforced site-wide for touch devices in index.css).

import React from 'react';

export default function MobileTabSelect({ tabs, active, onChange, variant = 'tab', ariaLabel }) {
    const isSub = variant === 'sub';
    const value = active >= 0 && active < tabs.length ? active : 0;

    return (
        <div style={isSub ? { ...S.wrap, ...S.wrapSub } : S.wrap}>
            <select
                value={value}
                onChange={e => onChange(Number(e.target.value))}
                aria-label={ariaLabel || (isSub ? 'Select sub-tab' : 'Select tab')}
                style={isSub ? { ...S.select, ...S.selectSub } : S.select}
            >
                {tabs.map((tab, i) => (
                    <option key={`${i}-${tab}`} value={i} style={S.option}>
                        {tab}
                    </option>
                ))}
            </select>
            {/* SVG chevron (2026-10-28): explicit pixel size, unlike a "▾"
                text glyph whose size varies by font and renders small on iOS. */}
            <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                width={isSub ? 20 : 24}
                height={isSub ? 20 : 24}
                style={isSub ? { ...S.chevron, ...S.chevronSub } : S.chevron}
            >
                <path d="M5 8.5l7 7 7-7" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        </div>
    );
}

const S = {
    wrap: {
        position: 'relative',
        width: '100%',
        minWidth: 0,
    },
    wrapSub: {
        marginBottom: '4px',
    },
    select: {
        display: 'block',
        width: '100%',
        boxSizing: 'border-box',
        appearance: 'none',
        WebkitAppearance: 'none',
        MozAppearance: 'none',
        backgroundColor: '#1e1e1e',
        border: '1px solid #2a2a2a',
        borderBottom: '2px solid #bb86fc',
        borderRadius: '8px 8px 0 0',
        color: '#bb86fc',
        fontSize: '16px',
        fontWeight: '600',
        fontFamily: 'inherit',
        letterSpacing: '0.3px',
        padding: '12px 52px 12px 16px',
        height: 'auto',
        lineHeight: '1.3',
        cursor: 'pointer',
        outline: 'none',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
    },
    selectSub: {
        backgroundColor: '#2c2c2c',
        border: '1px solid #444',
        // Explicit: overriding the border shorthand alone does not reset the
        // base style's purple borderBottom (React applies keys in base order).
        borderBottom: '1px solid #444',
        borderRadius: '6px',
        color: '#e0e0e0',
        fontWeight: '500',
        letterSpacing: 'normal',
        padding: '10px 46px 10px 14px',
    },
    // Android renders options with the select's own colors; iOS ignores this
    // and uses its native picker.
    option: {
        backgroundColor: '#1e1e1e',
        color: '#e0e0e0',
    },
    chevron: {
        position: 'absolute',
        right: '14px',
        top: '50%',
        transform: 'translateY(-50%)',
        color: '#bb86fc',
        pointerEvents: 'none',
        display: 'block',
    },
    chevronSub: {
        right: '12px',
        color: '#aaa',
    },
};
